import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './config'; // Import Firebase authentication, Firestore

// Function to fetch user profile
export const fetchUserProfile = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userSnapshot = await getDoc(userRef);
  return userSnapshot.exists() ? { id: userSnapshot.id, ...userSnapshot.data() } : null;
};

// Function to create user profile
export const createUserProfile = async (userId, profileData) => {
  return updateUserProfile(userId, profileData);
};

/**
 * Create/update user profile.
 * Wave 9: any payload that includes `username` MUST go through
 * createOrUpdateUserProfile (transactional usernames/{normalized} reservation).
 * Direct client writes of `username` are denied by Firestore rules.
 */
export const updateUserProfile = async (userId, profileData) => {
  const clean = { ...profileData };
  if (typeof clean.profileImageUrl === 'string') {
    clean.profileImageUrl = clean.profileImageUrl.replace(/&amp;/g, '&').replace(/&#(38|x26);/gi, '&');
  }
  if (typeof clean.coverImageUrl === 'string') {
    clean.coverImageUrl = clean.coverImageUrl.replace(/&amp;/g, '&').replace(/&#(38|x26);/gi, '&');
  }

  const hasUsername = Object.prototype.hasOwnProperty.call(clean, 'username');
  if (hasUsername) {
    const { createOrUpdateUserProfileFunction } = await import('./functionsService');
    await createOrUpdateUserProfileFunction(userId, clean);
    return;
  }

  // Non-username patches may still write directly (allowlisted safe fields).
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, clean, { merge: true });
};

/**
 * Evaluate profile completeness from an already-loaded profile object.
 * Prefer this over checkProfileCompleteness() to avoid a duplicate users/{uid} read.
 */
export const evaluateProfileCompleteness = (currentUser, userData) => {
  if (!currentUser) {
    return { isComplete: false };
  }
  if (!userData) {
    return { isComplete: false, userId: currentUser.uid };
  }

  const username = userData?.username || '';
  const email = userData?.email || currentUser.email || '';
  const firstName = userData?.firstName || '';
  const lastName = userData?.lastName || '';
  const displayName = userData?.displayName || currentUser.displayName || '';
  const profileCompleted = userData?.profileCompleted;
  const registrationProvider = userData?.registrationProvider;

  const isSocialLogin =
    registrationProvider === 'google.com' ||
    registrationProvider === 'facebook.com' ||
    currentUser.providerData.some(
      (p) => p.providerId === 'google.com' || p.providerId === 'facebook.com'
    );

  const hasReasonableData = email || firstName || lastName || displayName;
  const hasFirstLastName = firstName && lastName;
  const hasUsername = username && username.length >= 3;
  const hasDisplayName = displayName && displayName.length > 0;
  const hasEmail = email && email.includes('@');

  // AUTH-22: explicit stamp always wins
  if (profileCompleted === true) {
    return { isComplete: true, userId: currentUser.uid };
  }

  if (isSocialLogin) {
    // Returning social user with a reserved username is complete even if the
    // profileCompleted stamp was never written (legacy / partial writes).
    if (hasUsername && hasEmail && (hasFirstLastName || hasDisplayName || hasReasonableData)) {
      return { isComplete: true, userId: currentUser.uid, shouldMarkComplete: true };
    }
    return { isComplete: false, userId: currentUser.uid };
  }

  if (profileCompleted === false && !hasReasonableData) {
    return { isComplete: false, userId: currentUser.uid };
  }

  // Email users: username reserved OR legacy reasonable data → complete
  if (hasUsername && hasEmail) {
    return { isComplete: true, userId: currentUser.uid, shouldMarkComplete: true };
  }

  if (hasReasonableData) {
    return { isComplete: true, userId: currentUser.uid, shouldMarkComplete: true };
  }

  return { isComplete: false, userId: currentUser.uid };
};

// Function to check profile completeness (optional preloaded profile skips getDoc)
export const checkProfileCompleteness = async (preloadedProfile = null) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return { isComplete: false };
    }

    let userData = preloadedProfile;
    const userDocRef = doc(db, 'users', currentUser.uid);

    if (!userData) {
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        return { isComplete: false, userId: currentUser.uid };
      }
      userData = userDoc.data();
    }

    const result = evaluateProfileCompleteness(currentUser, userData);

    // Legacy: auto-stamp profileCompleted when inferred complete from fields
    if (result.shouldMarkComplete) {
      try {
        await updateDoc(userDocRef, {
          profileCompleted: true,
          updatedAt: new Date(),
        });
      } catch (updateError) {
        console.warn('Could not update profileCompleted field:', updateError);
      }
    }

    return { isComplete: result.isComplete, userId: result.userId };
  } catch (error) {
    console.error('Error checking profile completeness:', error);
    return { isComplete: false };
  }
};
